import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Checkbox } from "@arcadeai/design-system/components/ui/atoms/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@arcadeai/design-system/components/ui/atoms/dropdown-menu";
import { useNavigate } from "@tanstack/react-router";
import { Filter } from "lucide-react";

export type DataTableFacetedFilterOption = {
  label: string;
  value: string;
  icon?: React.ElementType;
};

type ServersFiltersProps = {
  searchTerm: string;
  serverTypes: string[];
  statuses: string[];
  typeOptions: DataTableFacetedFilterOption[];
  statusOptions: DataTableFacetedFilterOption[];
};

export function ServersFilters({
  searchTerm,
  serverTypes,
  statuses,
  typeOptions,
  statusOptions,
}: ServersFiltersProps) {
  const navigate = useNavigate();

  // Check if any filters are active
  const hasActiveFilters =
    searchTerm || serverTypes.length > 0 || statuses.length > 0;

  // Clear all filters function
  const clearAllFilters = () => {
    navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        name: "",
        serverTypes: [],
        statuses: [],
      }),
    });
  };

  const handleServerTypeChange = (optionValue: string, checked: boolean) => {
    const newValues = checked
      ? [...serverTypes, optionValue]
      : serverTypes.filter((v) => v !== optionValue);
    navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        serverTypes: newValues,
      }),
    });
  };

  const handleStatusChange = (optionValue: string, checked: boolean) => {
    const newValues = checked
      ? [...statuses, optionValue]
      : statuses.filter((v) => v !== optionValue);
    navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        statuses: newValues,
      }),
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button className="h-9 border-dashed" size="sm" variant="outline" />
        }
      >
        <Filter className="mr-2 size-4" />
        <span className="hidden lg:block">Filters</span>
        {!!hasActiveFilters && (
          <Badge
            className="ml-2 rounded-sm px-1 font-normal"
            variant="secondary"
          >
            {[searchTerm ? 1 : 0, serverTypes.length, statuses.length].reduce(
              (a, b) => a + b,
              0
            )}
          </Badge>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[200px]">
        <DropdownMenuLabel>Type</DropdownMenuLabel>
        {typeOptions.map((option) => {
          const isChecked = serverTypes.includes(option.value);
          return (
            <DropdownMenuItem
              className="cursor-pointer"
              key={option.value}
              onClick={() => handleServerTypeChange(option.value, !isChecked)}
              onSelect={(event) => {
                event.preventDefault();
              }}
            >
              <div className="flex items-center space-x-2">
                <Checkbox checked={isChecked} />
                <span>{option.label}</span>
              </div>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Status</DropdownMenuLabel>
        {statusOptions.map((option) => {
          const isChecked = statuses.includes(option.value);
          return (
            <DropdownMenuItem
              className="cursor-pointer"
              key={option.value}
              onClick={() => handleStatusChange(option.value, !isChecked)}
              onSelect={(event) => {
                event.preventDefault();
              }}
            >
              <div className="flex items-center space-x-2">
                <Checkbox checked={isChecked} />
                <span>{option.label}</span>
              </div>
            </DropdownMenuItem>
          );
        })}
        {!!hasActiveFilters && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="justify-center"
              onClick={() => clearAllFilters()}
            >
              Clear filters
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
